import type { ReactNode } from "react";
import { View } from "react-native";
import { Text } from "react-native-paper";
import { VictoryPie } from "victory-native";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { GAUGE_SEGMENTS } from "./constants";

const CONFIDENCE_COLORS = {
	lowConfidence: "#d66565",
	moderateConfidence: "#dda53a",
	satisfactoryConfidence: "#9ccc65",
	highConfidence: "#66bb68",
} as const;

type ProgressGaugeProps = {
	asset: "text" | "icon";
	progress: number;
	label?: string;
	icon?: ReactNode;
	size?: "small" | "medium" | "large";
	value?: string;
};

export const ProgressGauge = ({
	asset,
	progress,
	label,
	icon,
	size = "medium",
	value,
}: ProgressGaugeProps) => {
	const theme = useAppTheme();

	const SIZES = {
		small: { diameter: 40, strokeWidth: 2, fontSize: 8, backgroundSize: 21 },
		medium: { diameter: 64, strokeWidth: 4, fontSize: 10, backgroundSize: 34 },
		large: { diameter: 80, strokeWidth: 5, fontSize: 13, backgroundSize: 42 },
	};

	const dimensions = SIZES[size];

	const getProgressColor = (val: number): string => {
		if (val <= 25) {
			return theme.colors.lowConfidence ?? CONFIDENCE_COLORS.lowConfidence;
		}
		if (val <= 50) {
			return (
				theme.colors.moderateConfidence ?? CONFIDENCE_COLORS.moderateConfidence
			);
		}
		if (val <= 75) {
			return (
				theme.colors.satisfactoryConfidence ??
				CONFIDENCE_COLORS.satisfactoryConfidence
			);
		}
		return theme.colors.highConfidence ?? CONFIDENCE_COLORS.highConfidence;
	};

	const getGaugeBackgroundColor = (
		color: string,
		opacity: number = GAUGE_SEGMENTS.backgroundOpacity,
	): string => {
		const hexColor =
			typeof color === "string" && color.startsWith("#")
				? color
				: (theme.colors.highConfidence ?? CONFIDENCE_COLORS.highConfidence);
		if (typeof hexColor !== "string" || !hexColor.startsWith("#")) {
			return `rgba(102, 187, 104, ${opacity})`;
		}
		const r = Number.parseInt(hexColor.slice(1, 3), 16);
		const g = Number.parseInt(hexColor.slice(3, 5), 16);
		const b = Number.parseInt(hexColor.slice(5, 7), 16);
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	};

	const progressColor = getProgressColor(progress);
	const clampedProgress = Math.min(Math.max(progress, 0), 100);

	const totalSegments = GAUGE_SEGMENTS.total;
	const segmentsToColor = Math.ceil((clampedProgress / 100) * totalSegments);

	const segmentSize = GAUGE_SEGMENTS.arcDegrees;
	const gapSize = GAUGE_SEGMENTS.gapDegrees;
	const bottomGapSize = GAUGE_SEGMENTS.bottomGapDegrees;

	const pieData: Array<{ x: string; y: number }> = [];
	const colorScale: string[] = [];

	pieData.push({ x: "bottom-gap", y: bottomGapSize });
	colorScale.push("transparent");

	for (let i = 0; i < totalSegments; i++) {
		pieData.push({ x: `segment-${i}`, y: segmentSize });
		colorScale.push(
			i < segmentsToColor
				? progressColor
				: (theme.colors.progress?.none ?? theme.colors.surfaceVariant),
		);

		if (i < totalSegments - 1) {
			pieData.push({ x: `gap-${i}`, y: gapSize });
			colorScale.push("transparent");
		}
	}

	return (
		<Box alignItems="center" gap={8} style={{ width: "100%" }}>
			{label && (
				<Text
					variant="labelSmall"
					numberOfLines={1}
					style={{ textAlign: "center", alignSelf: "stretch" }}
				>
					{label}
				</Text>
			)}

			<Box
				alignItems="center"
				justifyContent="center"
				style={{
					width: dimensions.diameter,
					height: dimensions.diameter,
					position: "relative",
				}}
			>
				<View
					style={{
						position: "absolute",
						width: dimensions.backgroundSize,
						height: dimensions.backgroundSize,
						borderRadius: dimensions.backgroundSize / 2,
						backgroundColor: getGaugeBackgroundColor(progressColor),
					}}
				/>

				<VictoryPie
					data={pieData}
					colorScale={colorScale}
					innerRadius={dimensions.diameter / 2 - dimensions.strokeWidth}
					radius={dimensions.diameter / 2}
					cornerRadius={dimensions.strokeWidth / 2}
					width={dimensions.diameter}
					height={dimensions.diameter}
					padding={0}
					labels={() => null}
					startAngle={150}
					endAngle={510}
				/>

				<Box
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{asset === "text" ? (
						<Text
							style={{
								fontSize: dimensions.fontSize,
								fontWeight: "bold",
								color: progressColor,
							}}
						>
							{Math.round(clampedProgress)}%
						</Text>
					) : (
						icon
					)}
				</Box>
			</Box>

			{value && (
				<Text
					variant="labelSmall"
					style={{ color: theme.colors.outline, textAlign: "center" }}
				>
					{value}
				</Text>
			)}
		</Box>
	);
};
